import _ from "lodash";
import axios from "axios";

import IMeta from "models/IMeta";
import { setLoading } from "../app/actions";
import generateQuery, { generateSorting } from "helpers/query";
import { getTranslation, getOptions } from "helpers/common";
import Swal from "helpers/swal";
import { invoiceSorting as defaultSorting } from "constants/sorting";
import { ROLE_IDS, CUSTOM_SETTINGS } from "constants/.";

export const actionTypes = {
  GET_ORDERS_REQUEST: "GET_ORDERS_REQUEST",
  GET_ORDERS_SUCCESS: "GET_ORDERS_SUCCESS",
  GET_ORDERS_FAILURE: "GET_ORDERS_FAILURE",
  GET_ORDER_REQUEST: "GET_ORDER_REQUEST",
  GET_ORDER_SUCCESS: "GET_ORDER_SUCCESS",
  GET_ORDER_LINES_REQUEST: "GET_ORDER_LINES_REQUEST",
  GET_ORDER_LINES_SUCCESS: "GET_ORDER_LINES_SUCCESS",
  GET_ORDER_DELIVERIES_REQUEST: "GET_ORDER_DELIVERIES_REQUEST",
  GET_ORDER_DELIVERIES_SUCCESS: "GET_ORDER_DELIVERIES_SUCCESS",
  GET_ORDER_RECEIPTS_RETURNS_REQUEST: "GET_ORDER_RECEIPTS_RETURNS_REQUEST",
  GET_ORDER_RECEIPTS_RETURNS_SUCCESS: "GET_ORDER_RECEIPTS_RETURNS_SUCCESS",
  GET_ORDER_INVOICES_REQUEST: "GET_ORDER_INVOICES_REQUEST",
  GET_ORDER_INVOICES_SUCCESS: "GET_ORDER_INVOICES_SUCCESS",
  GET_ORDER_VERSIONS_REQUEST: "GET_ORDER_VERSIONS_REQUEST",
  GET_ORDER_VERSIONS_SUCCESS: "GET_ORDER_VERSIONS_SUCCESS",
  GET_ORDER_EXPORT_DATA_REQUEST: "GET_ORDER_EXPORT_DATA_REQUEST",
  GET_ORDER_EXPORT_DATA_SUCCESS: "GET_ORDER_EXPORT_DATA_SUCCESS",
  MARK_ORDER_LINE_DELIVERED_REQUEST: "MARK_ORDER_LINE_DELIVERED_REQUEST",
  MARK_ORDER_LINE_DELIVERED_SUCCESS: "MARK_ORDER_LINE_DELIVERED_SUCCESS",
  GET_ORDER_SELECT_OPTIONS_REQUEST: "GET_ORDER_SELECT_OPTIONS_REQUEST",
  GET_ORDER_SELECT_OPTIONS_SUCCESS: "GET_ORDER_SELECT_OPTIONS_SUCCESS"
};

export default class Actions {
  public static getOrders(meta: IMeta = {}) {
    return async (dispatch, getState) => {
      dispatch(request());
      dispatch(setLoading(true));

      if (_.isEmpty(meta)) {
        meta = _.get(getState(), "orders.meta", {});
      }

      if (!meta.pageSize) {
        const savedSettings = JSON.parse(
          localStorage.getItem("customSettings") || "{}"
        );
        _.set(
          meta,
          "pageSize",
          _.get(savedSettings, `${CUSTOM_SETTINGS.orders}.pageSize`, 10)
        );
      }

      const url = `OrdersExtended?$expand=DeliveryAddress,OtherRelation,Supplier,Seller,Lines/Item
        &$select=Id,DocumentId,Number,Date,VersionNumber,StateId,TypeId,PaymentDate,
        InCoverScheme,AdvancePayment,RoleId,HarvestYear,DeliveryAddress/Name,DeliveryAddress/Address1,
        DeliveryAddress/ZipCode,DeliveryAddress/City,VersionDate,TotalAmount,DeliveryPeriodStart,
        InCoverScheme,DeliveryPeriodEnd,OtherRelation/Id,OtherRelation/Name,OtherRelation/Number,Supplier/Name,
        Supplier/Number,Seller/Name,Seller/Number,Bio,Read
        ${generateQuery(meta)}`;

      try {
        const { data } = await axios.get(url);
        const payload = _.get(data, "value", []);
        const newMeta = {
          ...meta,
          count: +_.get(data, "odata.count", 0)
        };
        dispatch(success(payload, newMeta));
        dispatch(setLoading(false));
      } catch (error) {
        dispatch(failure(error));
        dispatch(setLoading(false));
      }
    };

    function request() {
      return { type: actionTypes.GET_ORDERS_REQUEST };
    }
    function success(payload, meta) {
      return { type: actionTypes.GET_ORDERS_SUCCESS, payload, meta };
    }
    function failure(error) {
      return { type: actionTypes.GET_ORDERS_FAILURE, error };
    }
  }

  public static getOrder(id, roleId = "") {
    return async dispatch => {
      dispatch(request());
      dispatch(setLoading(true));
      const roleLabel = roleId.split("=")[1];

      const roleIdFilter =
        roleId.length > 0 ? ` and RoleId eq '${ROLE_IDS[roleLabel]}'` : "";

      const url = `OrdersExtended?$filter=Id eq ${id}${roleIdFilter}&$expand=DeliveryAddress/Country,
        OtherRelation/PrimaryAddress/Country,Supplier/PrimaryAddress/Country,Buyer,
        Seller/PrimaryAddress/Country,BuyerAgent,BuyerAgentTeam,SellerAgent,SellerAgentTeam
        &$select=Id,DocumentId,RoleId,Number,Date,VersionNumber,StateId,TypeId,PaymentDate,
        DeliveryTypeId,InCoverScheme,HarvestYear,VersionDate,
        ExternalReferenceNumber,AdvancePayment,Bio,SkalNumber,
        TotalAmount,DeliveryPeriodStart,InCoverScheme,DeliveryPeriodEnd,
        BuyerAgentCommission,SellerAgentCommission,DeliveryAddress,
        DeliveryAddress/Country,OtherRelation/Name,OtherRelation/Number,
        OtherRelation/OfficeNumber,OtherRelation/MailAddress,
        OtherRelation/PrimaryAddress,OtherRelation/PrimaryAddress/Country,Supplier/Name,
        Supplier/Number,Supplier/OfficeNumber,
        Supplier/MailAddress,Supplier/PrimaryAddress,Supplier/PrimaryAddress/Country,
        Seller/Name,Seller/Number,BuyerAgent/Name,BuyerAgentTeam/Description,
        SellerAgent/Name,SellerAgentTeam/Description,Buyer/Name`;

      try {
        const {
          data: {
            value: [order]
          }
        } = await axios.get(url);
        dispatch(success(order));
        dispatch(setLoading(false));
      } catch (error) {
        dispatch(failure(error));
        dispatch(setLoading(false));
      }
    };

    function request() {
      return { type: actionTypes.GET_ORDER_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_ORDER_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_ORDERS_FAILURE, error };
    }
  }

  public static getOrderLines(id) {
    return async dispatch => {
      dispatch(request());

      const url = `OrderLines?$filter=OrderId eq ${id}
        &$expand=QuantityUnit,Item,ProductValue1,PriceUnit
        &$select=Id,Number,Quantity,QuantityUnit/Description,ShippedQuantity,ShippedCompletely,
        Item/Description,GrowerClaims,ProductValue1/Description,Price,PriceUnit/Description,
        OrderedAmount,Bio,PendingQuantity
        &$orderby=Number asc`;

      try {
        const {
          data: { value }
        } = await axios.get(url);
        dispatch(success(value));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_ORDER_LINES_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_ORDER_LINES_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_ORDERS_FAILURE, error };
    }
  }

  public static getOrderDeliveries(id, roleId) {
    return async dispatch => {
      dispatch(request());

      const url = `ShipmentsExtended?$expand=OtherRelation,Lines/OrderLine,Supplier,Seller
          &$select=Id,DeliveryId,RoleId,Date,DeliveryDate,OtherRelation/Name,OtherRelation/Number,
          Seller/Name,Seller/Number,Seller/FytosanitairNumber,Supplier/Name,Supplier/Number,Bio,DocumentId
          &$filter=Lines/any(l:l/OrderLine/OrderId eq ${id})
          and RoleId eq '${ROLE_IDS[roleId]}' and IsFinal
          &$orderby=Date desc,DeliveryId desc`;

      try {
        const {
          data: { value }
        } = await axios.get(url);
        dispatch(success(value));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_ORDER_DELIVERIES_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_ORDER_DELIVERIES_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_ORDERS_FAILURE, error };
    }
  }

  public static getOrderReceiptsReturns(id, roleId) {
    return async dispatch => {
      dispatch(request());

      const url = `ReceiptsAndReturnsExtended?$expand=OtherRelation,Supplier,Seller,Order
        &$select=Id,DocumentId,ReceiptId,RoleId,Date,ReceiptDate,OtherRelation/Name,OtherRelation/Number,
        Seller/Name,Seller/Number,Supplier/Name,Supplier/Number,Order/Number,OrderId
        &$filter=OrderId eq ${id} and RoleId eq '${ROLE_IDS[roleId]}'
        &$orderby=Date desc,ReceiptId desc`;

      try {
        const {
          data: { value }
        } = await axios.get(url);
        dispatch(success(value));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_ORDER_RECEIPTS_RETURNS_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_ORDER_RECEIPTS_RETURNS_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_ORDERS_FAILURE, error };
    }
  }

  public static getOrderInvoices(id, roleId) {
    return async (dispatch, getState) => {
      dispatch(request());
      const roleIds = _.get(getState(), "app.roleIds", []);
      const role = _.find(roleIds, ["label", roleId]);

      const getDerivativeFilter = () => {
        switch (role && role.value) {
          case "Buyer":
            return " and DerivativeOrder eq false";
          case "Seller":
            return "";
          case "Supplier":
            return " and DerivativeOrder eq true";
          default:
            return "";
        }
      };

      const url = `Invoices?$expand=CounterParty,Order
        &$select=Id,DocumentId,Number,PurchaseType,InvoiceDate,PaymentDate,CounterParty/Number,
        CounterParty/Name,InvoiceTypePortal,InvoiceAmountIncVat,PaidAmount,RemainingAmount,Order/Number
        &$orderby=${generateSorting(defaultSorting)}
        &$filter=CustomerId eq ${
          getState().app.currentCustomer.Id
        } and Order/Id eq ${id}${getDerivativeFilter()}`;

      try {
        const {
          data: { value }
        } = await axios.get(url);
        dispatch(success(value));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_ORDER_INVOICES_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_ORDER_INVOICES_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_ORDERS_FAILURE, error };
    }
  }

  public static getOrderVersions(id, roleId) {
    return async dispatch => {
      dispatch(request());

      const url = `OrderVersions?$filter=OrderId eq ${id}&$expand=Order,BuyerDocument,SellerDocument,SupplierDocument
        &$select=Order/Number,Date,VersionNumber,BuyerDocument/Id,BuyerDocument/Url,SellerDocument/Id,
        SellerDocument/Url,SupplierDocument/Id,SupplierDocument/Url`;

      try {
        const {
          data: { value }
        } = await axios.get(url);
        const orderVersions = _(value)
          .map(version => ({
            ...version,
            DocumentId: _.get(version, `${ROLE_IDS[roleId]}Document.Id`)
          }))
          .orderBy(v => _.toNumber(v.VersionNumber), "desc")
          .value();
        dispatch(success(orderVersions));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_ORDER_VERSIONS_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_ORDER_VERSIONS_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_ORDERS_FAILURE, error };
    }
  }

  public static async getExportData(searchString) {
    const url = `OrdersExtended?$expand=Document,OtherRelation,Supplier,Seller,
      BuyerAgent,SellerAgent,Lines/QuantityUnit,Lines/Item,Lines/Item/ProductType,
      Lines/Item/ProductType/ProductGroup,Lines/Item/ProductType/ProductGroup/ProductCategory,
      Lines/ProductValue1,Lines/ProductValue2,Lines/ProductValue3,Lines/PriceUnit
      &$select=Id,Document/Id,Document/Url,Number,VersionNumber,StateId,RoleId,
      TypeId,Date,DeliveryPeriodStart,OtherRelation/Name,OtherRelation/Number,
      PaymentDateArrangementTypeId,DateModified,ExternalReferenceNumber,HarvestYear,
      TotalAmount,PaymentDate,AdvancePayment,InCoverScheme,DeliveryPeriodEnd,
      Supplier/Name,Seller/Name,BuyerAgent/Name,SellerAgent/Name,Lines/Number,
      Lines/Item/ProductType/ProductGroup/ProductCategory/Description,Lines/ProductValue1/Description,
      Lines/ProductValue2/Description,Lines/ProductValue3/Description,Lines/Price,
      Lines/PriceUnit/Description,Lines/Quantity,Lines/QuantityUnit/Description,
      Lines/ShippedQuantity,Lines/ShippedCompletely,Lines/OrderedAmount,Lines/Bio,
      Lines/GrowerClaims,Lines/Item/ProductType/ProductGroup/Description,
      Lines/Item/ProductType/Description,Lines/Item/Description,Lines/Item/CultivarDescription
      ${generateQuery({ searchString, findAll: true }, "(RelationId")})
      &$orderby=Date asc, Number asc`;

    try {
      const {
        data: { value }
      } = await axios.get(url);
      return value;
    } catch (error) {
      return error;
    }
  }

  public static markAsDelivered(ids, orderId) {
    return dispatch => {
      if (_.isEmpty(ids)) {
        Swal.alert("Info", getTranslation("MINIMUM_ONE_ORDER"));
      } else {
        Swal.confirm({
          title: getTranslation("SURE"),
          text: getTranslation("CANNOT_UNDO")
        }).then(async result => {
          if (result.value) {
            dispatch(request());
            try {
              await axios.all(
                ids.map(id =>
                  axios.patch(`OrderLines(${id})`, {
                    ShippedCompletely: true
                  })
                )
              );

              dispatch(Actions.getOrderLines(orderId));
              Swal.toast(getTranslation("SUCCESS"));
            } catch (error) {
              dispatch(failure(error));
            }
          }
        });
      }
    };

    function request() {
      return { type: actionTypes.MARK_ORDER_LINE_DELIVERED_REQUEST };
    }
    function failure(error) {
      return { type: actionTypes.GET_ORDERS_FAILURE, error };
    }
  }

  public static getSelectOptions() {
    return async dispatch => {
      dispatch(request());

      try {
        const { data } = await axios.get("Orders/Enums");
        const statusOptions = getOptions(data, "OrderState");
        const typeOptions = getOptions(data, "OrderType");

        dispatch(success({ statusOptions, typeOptions }));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_ORDER_SELECT_OPTIONS_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_ORDER_SELECT_OPTIONS_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_ORDERS_FAILURE, error };
    }
  }
}
